<template>
  <div class="shipments-page">
    <page-header :title="$t('Delivery Orders')">
      <shipment :user="merchant" :type="type" :shipmentModal="shipmentModal" @shipmentModal="shipmentModal = $event" @loadShipments="loadShipments"></shipment>
    </page-header>

    <template v-if="!common.getPermissions('delivery_orders') || common.getPermissions('delivery_orders').base.includes('view')">
      <div class="md:flex md:flex-wrap grid grid-cols-2 gap-2 items-center whitespace-nowrap md:mb-6 mb-3">
        <request-button v-if="merchantStatus == 'active'" class="step-4" :selected="selected" @selected="selected = $event" @loadShipments="loadShipments"></request-button>
        <restart-orders v-if="merchantStatus == 'active'" :selected="selected" @selected="selected = $event" @loadShipments="loadShipments"></restart-orders>
        <print-label-button class="step-3" :selected="selected" :printLableStatuses="printLableStatuses" :checkAll="checkAll" :filters="statuses"></print-label-button>
        <download-button v-if="$store.state.AppActiveUser.userRole === 'merchant'" class="whitespace-nowrap" :selected="selected"></download-button>
        <cancel-shipments-button v-if="merchantStatus == 'active' && (!common.getPermissions('delivery_orders') || common.getPermissions('delivery_orders').base.includes('cancel'))" :selected="selected" @selected="selected = $event" @loadShipments="loadShipments"></cancel-shipments-button>
        <export-button class="whitespace-nowrap"></export-button>
        <div v-if="merchant.id === 5077 || releaseVersion === 'staging'">
          <download-labels-button v-if="$store.state.AppActiveUser.userRole === 'merchant'" class="whitespace-nowrap" :selected="selected"></download-labels-button>
        </div>
      </div>

      <NewShipBluTable
        :sst="true"
        class="step-1"
        orders
        multiple
        :checkAll="checkAll"
        :checkAllInPage="checkAllInPage"
        :selected="selected"
        :filters="statuses"
        v-model="selected"
        :max-items="maximumItems"
        @checkAll="checkAll = $event"
        @checkAllInPage="checkAllInPage = $event"
        @search="handleSearch"
        @keyup.enter.native="handleSearch"
        advancedSearch
        :searchCriteria="searchCriteria"
        @selectedCriteria="selectedCriteria = $event"
        pagination
        :data="shipments"
        :tableLoader="tableLoader"
      >
        <template slot="header">
          <div class="lg:w-auto w-full md:mt-0 lg:flex grid grid-cols-3 gap-2 items-center">
            <table-filter icon="BarChart2Icon" filterLabel="Status" :filters="statuses" :statusFilters="statusOptions" @changeFilter="changeStatus" @filterValue="statusValue = $event"/>
            <table-filter icon="FlagIcon" filterLabel="Flag" :filters="flagFilters" :statusFilters="flagOptions" @changeFilter="changeFlag" @filterValue="flagValue = $event"/>
            <date-filter filterLabel="date"  @searchDate="handleSearchDate" ref="filterDateModal" />
          </div>
        </template>
        <template slot="thead">
          <shipblu-th>{{$t('Tracking No.')}}</shipblu-th>
          <shipblu-th>{{$t('Created')}}</shipblu-th>
          <shipblu-th>{{$t('Received')}}</shipblu-th>
          <shipblu-th>{{$t('Customer')}}</shipblu-th>
          <shipblu-th>{{$t('Location')}}</shipblu-th>
          <shipblu-th v-if="!common.getPermissions('delivery_orders') || common.getPermissions('delivery_orders').cash_amount.includes('view')">{{$t('Cash Amount')}}</shipblu-th>
          <shipblu-th>{{$t('Refernce Number')}}</shipblu-th>
          <shipblu-th>{{$t('Status')}}</shipblu-th>
          <shipblu-th>{{$t('Attempts')}}</shipblu-th>
          <shipblu-th class="step-12 order-1 flex justify-center" v-if="(!$route.params.merchantID && !$route.params.warehouseID) || $store.state.AppActiveUser.userRole === 'customer-support'">
            <feather-icon icon="ChevronDownIcon" svgClasses="h-5 w-5"/>
          </shipblu-th>
        </template>

        <template slot-scope="{ data }">
          <shipblu-tr :data="tr" viewInPanel :key="indextr" v-for="(tr, indextr) in data" :color="`${getStyle(data[indextr])}`" :to="{ query: { ...$route.query, order: data[indextr].id } }">
            <shipblu-td class="sm:hidden block col-span-2 p-0 order-3">
              <!-- line -->
              <p style="background-color: #DCDCDC;height: 1px;"></p>
            </shipblu-td>

            <shipblu-td :data="data[indextr].tracking_number" class="text-left sm:order-3 order-13">
              <div class="sm:static absolute top-3 transform-50">
                <p class="flex gap-1 items-center sm:text-base text-lg">
                  <span class="sm:order-2 order-1">{{ data[indextr].tracking_number }}</span>
                  <shipblu-icon @click.stop.prevent="common.copied(data[indextr].tracking_number)" iconLibrary="material" iconClass="text-grey hover:text-primary text-xl cursor-pointer sm:order-1 order-2" iconName="content_copy"></shipblu-icon>
                </p>
                <div class="flex gap-1 items-center sm:mt-2 mt-1 sm:justify-start justify-center">
                  <div v-if="data[indextr].fulfillment_provider_metadata" class="flex gap-1 px-1.5 items-center border-solid border border-grey-dark rounded-4xl h-5">
                    <feather-icon icon="DatabaseIcon" svgClasses="w-3 h-3"/>
                    <p class="font-medium text-xs leading-none">{{$t('FL')}}</p>
                  </div>
                  <p class="text-sm sm:text-left text-center" style="color: #F68B26;">{{ data[indextr].on_hold ? 'On Hold' : ''}} {{ data[indextr].rto_requested && data[indextr].on_hold ? ' - ' : '' }} {{ data[indextr].rto_requested ? 'RTO' : '' }}</p>
                </div>
              </div>
            </shipblu-td>

            <shipblu-td :data="data[indextr].created" class="order-4">
              <p class="sm:hidden block text-secondary text-sm mb-2">{{$t('Created')}}</p>
              <p class="sm:text-base text-sm">{{ common.formatDate(data[indextr].created, $i18n.locale) }}</p>
              <p class="text-sm sm:text-secondary sm:mt-2 mt-1">{{ $i18n.locale === 'en' ? common.gettingDate(data[indextr].created).split(',')[0] : common.gettingDate(data[indextr].created).split('،')[0] }}</p>
            </shipblu-td>

            <shipblu-td :data="data[indextr].pickupDate" class="order-5">
              <p class="sm:hidden block text-secondary text-sm mb-2">{{$t('Received')}}</p>
              <p class="sm:text-base text-sm">{{ data[indextr].pickup_date?  new Date(data[indextr].pickup_date).toLocaleDateString('fr-CA') : 'N/A' }}</p>
              <p class="text-sm sm:text-secondary sm:mt-2 mt-1">{{ data[indextr].pickup_date ? ($i18n.locale === 'en' ? common.gettingDate(data[indextr].pickup_date).split(',')[0] : common.gettingDate(data[indextr].pickup_date).split('،')[0]) : '' }}</p>
            </shipblu-td>

            <shipblu-td :data="data[indextr].customerInfo" class="order-6">
              <div>
                <p class="sm:hidden block text-secondary text-sm mb-2">{{$t('Customer')}}</p>
                <p class="sm:text-base text-sm">{{ data[indextr].customer.full_name }}</p>
                <p class="text-sm flex gap-1 sm:text-secondary sm:mt-2 mt-1">
                  <span class="sm:order-2 order-1">{{ data[indextr].customer.phone }}</span>
                  <shipblu-icon @click.stop.prevent="common.copied(data[indextr].customer.phone)" iconLibrary="material" iconClass="text-grey hover:text-primary text-xl cursor-pointer sm:order-1 order-2" iconName="content_copy"></shipblu-icon>
                </p>
              </div>
            </shipblu-td>

            <shipblu-td :data="data[indextr].location" class="order-7">
              <p class="sm:hidden block text-secondary text-sm mb-2">{{$t('Location')}}</p>
              <p class="sm:text-base text-sm">{{ $t(data[indextr].customer.address.zone.name) }}</p>
              <p class="text-sm sm:text-secondary sm:mt-2 mt-1">{{ $t(data[indextr].customer.address.zone.city.name) }}</p>
            </shipblu-td>

            <shipblu-td :data="data[indextr].packages" class="order-8" v-if="!common.getPermissions('delivery_orders') || common.getPermissions('delivery_orders').cash_amount.includes('view')">
              <p class="sm:hidden block text-secondary text-sm mb-2">{{$t('Cash Amount')}}</p>
              <p class="sm:text-base text-sm">{{ data[indextr].cash_amount && parseInt(data[indextr].cash_amount) > 0 ? ((Number(data[indextr].cash_amount)).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2})).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : 'N/A' }} </p>
              <p class="text-sm sm:text-secondary sm:mt-2 mt-1">{{ data[indextr].packages.length }} {{$t('Packages')}}</p>
            </shipblu-td>

            <shipblu-td :data="data[indextr].packages" class="order-8">
              <p class="sm:hidden block text-secondary text-sm mb-2">{{$t('Refernce Number')}}</p>
              <p class="sm:text-base text-sm">{{ data[indextr].merchant_order_reference ? data[indextr].merchant_order_reference : 'N/A' }} </p>
            </shipblu-td>

            <shipblu-td class="order-9" :data="data[indextr].status" style="white-space: nowrap;">
              <p class="sm:hidden block text-secondary text-sm mb-2">{{$t('Status')}}</p>
              <div class="flex" style="width: fit-content;" @click.stop.prevent="removeAttribute($event), openNotificationFailureData(data[indextr])">
                <span class="capitalize border border-solid rounded-4xl px-5 py-1.5 leading-none sm:text-sm text-xs text-white" :style="`background-color: ${getStyle(data[indextr])}`">{{ $t(getStatusLabel(data[indextr])) }}</span>
              </div>
              <p class="text-sm sm:text-secondary pl-2">{{ data[indextr].delivered_date ? common.formatDate(data[indextr].delivered_date, $i18n.locale) : '' }}</p>
            </shipblu-td>

            <shipblu-td :data="data[indextr].deliveredOn" class="order-10">
              <p class="sm:hidden block text-secondary text-sm mb-2">{{$t('Attempts')}}</p>
              <span class="sm:text-base text-sm">{{ data[indextr].delivery_attempts }}/3</span>
            </shipblu-td>

            <shipblu-td class="order-2" style="vertical-align: middle;" v-if="(!$route.params.merchantID && !$route.params.warehouseID) || $store.state.AppActiveUser.userRole === 'customer-support'">
              <div class="md:text-center text-right" @click.stop.prevent="removeAttribute($event)">
                <vs-dropdown
                  :disabled="merchantStatus == 'on_hold'"
                  vs-trigger-click
                  @click="selected = []"
                  class="dd-actions cursor-pointer"
                >
                  <span class="material-symbols-outlined text-2xl">more_horiz</span> 
                  <vs-dropdown-menu style="width: 190px">
                    <vs-dropdown-item
                      @click="viewShipment(data[indextr])"
                    >
                      <span class="flex items-center">
                        <feather-icon
                          icon="EyeIcon"
                          svgClasses="h-4 w-4"
                          class="mr-2"
                        />
                        <span>{{$t('View')}}</span>
                      </span>
                    </vs-dropdown-item>
                    <div v-if="$store.state.AppActiveUser.userRole !== 'customer-support'">
                      <!-- ALLOW CANCEL IF NOT DELIVERED OR RETURNED -->
                      <vs-dropdown-item v-if="(data[indextr].status === 'created' || data[indextr].status === 'pickup_requested') && !data[indextr].fulfillment_provider_metadata && merchantStatus == 'active' && (!common.getPermissions('delivery_orders') || common.getPermissions('delivery_orders').base.includes('cancel'))"
                        @click="updateShipmentStatus(data[indextr],'cancel this order?', 'cancelled')">
                        <span class="flex items-center">
                          <feather-icon
                            icon="XCircleIcon"
                            svgClasses="h-4 w-4"
                            class="mr-2"
                          />
                          <span>{{$t('Cancel Shipment')}}</span>
                        </span>
                      </vs-dropdown-item>
                      <vs-dropdown-item v-if="(data[indextr].status === 'picked_up' || data[indextr].status === 'in_transit' || data[indextr].status === 'out_for_delivery' || data[indextr].status === 'delivery_attempted') && merchantStatus == 'active'"
                        @click="updateShipmentStatus(data[indextr], 'return this order to origin?', 'return_to_origin')"
                      >
                        <span class="flex items-center">
                          <feather-icon
                            icon="XCircleIcon"
                            svgClasses="h-4 w-4"
                            class="mr-2"
                          />
                          <span>{{$t('Return to Origin')}}</span>
                        </span>
                      </vs-dropdown-item>
                      <!-- ACTIONS FOR DELIVERED -->
                      <vs-dropdown-item v-if="data[indextr].status === 'delivered' && merchantStatus == 'active'"
                        @click="returnOrderPackages({title: 'Return Packages', data: data[indextr]})">
                        <span class="flex items-center">
                          <feather-icon
                            icon="DeleteIcon"
                            svgClasses="h-4 w-4"
                            class="mr-2"
                          />
                          <span>{{$t('Pickup to Return')}}</span>
                        </span>
                      </vs-dropdown-item>
                      <vs-dropdown-item  v-if="data[indextr].status === 'delivered' && merchantStatus == 'active'"
                        @click="returnOrderPackages({title: 'Exchange Packages', data: data[indextr]})">
                        <span class="flex items-center">
                          <feather-icon
                            icon="Minimize2Icon"
                            svgClasses="h-4 w-4"
                            class="mr-2"
                          />
                          <span>{{$t('Pickup to Exchange')}}</span>
                        </span>
                      </vs-dropdown-item>
                      <!-- ACTIONS FOR CANCELLED -->
                      <vs-dropdown-item v-if="(data[indextr].status === 'cancelled') && merchantStatus == 'active'" 
                      @click="restartOrders(data[indextr])" >
                        <span class="flex items-center">
                          <feather-icon
                            icon="RotateCcwIcon"
                            svgClasses="h-4 w-4"
                            class="mr-2"
                          />
                          <span>{{$t('Restart Shipment')}}</span>
                        </span>
                      </vs-dropdown-item>
                    </div>
                  </vs-dropdown-menu>
                </vs-dropdown>
              </div>
            </shipblu-td>
          </shipblu-tr>
        </template>
      </NewShipBluTable>
      <shipblu-cursor-pagination :merchant="merchant" :maximumItems="maximumItems" :paginationObj="paginationObj" @changePageSize="changePageSize" @changePageNumber="changePageNumber($event)" />

      <div :class="$route.query.from ? 'block' : 'hidden'">
        <div :class="$route.query.from ? 'exchange-modal rounded-md' : ''">
          <div class="title py-4 flex">
            <img class="ml-6 mr-2" src="../../assets/images/pages/Group.svg"/>
            <p class="text-primary text-base font-medium">{{ $t('Exchange Request') }}</p>
          </div>
          <p class="py-4 px-6 text-base font-medium">{{ $t('Select the delivered order you want, and pickup to exchange!') }}</p>
        </div>
        <div :class="$route.query.from ? 'show-exchange-modal' : ''"></div>
      </div>
      <return-and-exchange-package :userMerchant="userMerchant" :title="title" :governorateId="governorateId" :merchant="merchant" :returnPackagesFlag="returnPackagesFlag" :returnPackagesData="returnPackagesData"
        @returnPackagesFlag="returnPackagesFlag = $event" @selected="selected = $event" @loadShipments="loadShipments" @refundFlag="refundFlag = $event"></return-and-exchange-package>

      <!-- Order View Panel -->
      <order-view-panel :showPanel="showPanel" :merchantStatus="merchantStatus" @updateShipmentStatus="updateShipmentStatus($event)" @returnOrderPackages="returnOrderPackages($event)" @restartOrders="restartOrders($event)" @orderData="orderData = $event"></order-view-panel>
    </template>

    <template v-else>
      <NoPermission :action="$t('view this page')"/>
    </template>
  </div>
</template>

<script>
import {sendRequest} from '../../http/axios/requestHelper.js'
import Shipment from './components/Shipment.vue'
import i18nData from '../../i18n/i18nData.js'
import common  from '@/assets/utils/common'
import ReturnAndExchangePackage from './components/ReturnAndExchangePackage.vue'
import NewShipBluTable from '../../layouts/components/NewShipBluTable.vue'
import ShipbluCursorPagination from '../../layouts/components/ShipbluCursorPagination.vue'
import ShipbluTr from '../../layouts/components/ShipBluTr.vue'
import ShipbluTh from '../../layouts/components/ShipBluTh.vue'
import ShipbluTd from '../../layouts/components/ShipBluTd.vue'
import ExportButton from '../headOfFleet/components/ExportButton.vue'
import PrintLabelButton from '../headOfFleet/components/PrintLabelButton.vue'
import CancelShipmentsButton from '../admin/components/CancelShipmentsButton.vue'
import RequestButton from './components/RequestButton.vue'
import ShipbluPrompt from '../../layouts/components/ShipBluPrompt.vue'
import RestartOrders from './components/RestartButton.vue'
import Shepherd from 'shepherd.js'
import 'shepherd.js/dist/css/shepherd.css'
import TableFilter from './components/TableFilter.vue'
import DateFilter from './components/DateFilter.vue'
import ResponsiveCheckBox from '../../layouts/components/ResponsiveCheckBox.vue'
import DownloadButton from '../admin/components/DownloadButton.vue'
import OrderViewPanel from './components/OrderViewPanel.vue'
import DownloadLabelsButton from '../admin/components/DownloadLabelsButton.vue'
import NoPermission from './components/NoPermission.vue'

export default {
  data () {
    return {
      common,
      releaseVersion: process.env.VUE_APP_RELEASE_VERSION,
      printLableStatuses: ['pickup_rescheduled', 'pickup_requested', 'created', 'out_for_pickup'],
      merchantUID: this.$store.state.AppActiveUser.uid,
      type: 'Add',
      shipmentModal: false,
      maximumItems: localStorage.getItem('maxItems') ? Number(localStorage.getItem('maxItems')) : process.env.VUE_APP_MAXIMUM_ITEMS_TABLE,
      shipmentURL: 'api/v1/delivery-orders/',
      selected: [],
      tableList: [
        'shipblu-th: Component',
        'shipblu-tr: Component',
        'shipblu-td: Component',
        'thread: Slot',
        'tbody: Slot',
        'header: Slot'
      ],
      shipments: [],
      merchant: {},
      settings: {
        maxScrollbarLength: 60,
        wheelSpeed: .60
      },
      searchVal: this.$route.query.search ? this.$route.query.search : '',
      shipment: {},
      paginationObj: {
        currentPage: 1,
        currentCursor: '',
        next: null,
        previous: null
      },
      merchantID: '',
      merchantStatus: '',
      title: '',
      returnPackagesFlag: false,
      returnPackagesData: {
        refund: false
      },
      file_URl: '',
      trackingData: {},
      trackingStatus: '',
      tourMerchantOrders: '',
      governorateId: '',
      searchInProgress: false,
      searchedValue: ' ',
      fromDate: '',
      toDate: '',
      criteria: '',
      userMerchant: {},
      tableLoader: false,
      tourForDelevryOrders:'',
      checkAll: false,
      printableStatuses: ['pickup_rescheduled', 'pickup_requested', 'created', 'out_for_pickup'],
      checkAllInPage: false,
      statusValue: '',
      flagValue:'',
      flagOptions:[
        {
          name: 'RTO Requested',
          value: 'rto_requested'
        },
        {
          name: 'On Hold',
          value: 'on_hold'
        }
      ],
      statusOptions: [
        {
          name: 'all',
          value: 'all'
        },
        {
          name: 'created',
          value: 'created'
        },
        {
          name: 'pickup requested',
          value: 'pickup_requested'
        },
        {
          name: 'out for pickup',
          value: 'out_for_pickup'
        },
        {
          name: 'pickup rescheduled',
          value: 'pickup_rescheduled'
        },
        {
          name: 'picked up',
          value: 'picked_up'
        },
        {
          name: 'in transit & en route',
          value: 'in_transit, en_route'
        },
        {
          name: 'out for delivery',
          value: 'out_for_delivery'
        },
        {
          name: 'delivery attempted',
          value: 'delivery_attempted'
        },
        {
          name: 'reattempt delivery',
          value: 'reattempt_delivery'
        },
        {
          name: 'delivered',
          value: 'delivered'
        },
        {
          name: 'out for return',
          value: 'out_for_return'
        },
        {
          name: 'Returned',
          value: 'returned'
        },
        {
          name: 'cancelled',
          value: 'cancelled'
        }
      ],
      statuses: [],
      flagFilters:[],
      showPanel: false,
      refundFlag: false,
      selectedCriteria: {},
      searchCriteria: [
        {
          label: 'Tracking No.',
          value: 'tracking_number'
        },
        {
          label: 'Reference No.',
          value: 'merchant_order_reference'
        },
        {
          label: 'Phone No.',
          value: 'phone'
        }
      ],
      orderData: {}
    }
  },
  watch: {
    '$route.query' () {
      if (this.$route.params.warehouseID && !this.$route.params.merchantID && !this.$route.query.flag && !this.$route.query.status && !this.$route.query.search) {
        this.flagFilters = [] 
        this.statuses = [] 
        this.loadWarehouseShipments()
      } else if (!this.$route.query.flag && !this.$route.query.status && !this.$route.query.search && !this.$route.query.order) {
        this.flagFilters = []  
        this.flagValue = ''
        this.statuses = []  
        this.statusValue = ''
      }  else if (this.$route.query.from) {
        this.flagFilters = [this.$route.query.flag]
        this.flagValue = this.$route.query.flag || ''
        this.statuses = [this.$route.query.status]
        this.statusValue = this.$route.query.status || ''
        this.loadShipments(this.paginationObj.currentCursor)
      }
      if (this.$route.query.order) {
        this.showPanel = true
      } else {
        this.shipments = this.shipments.map(order => {
          return order.id === this.orderData.id ? { ...order, ...this.orderData } : order
        })
        this.showPanel = false
      }
    },
    searchVal () {
      if (this.$route.query.flag) {
        this.$router.push({
          query: {
            search: this.searchVal,
            flag: this.flagValue
          }
        }).catch(() => {})
      } else {
        this.$router.push({
          query: {
            search: this.searchVal,
            status: this.statusValue
          }
        }).catch(() => {})
      }
    },
    refundFlag (value) {
      this.returnPackagesData = {
        ...this.returnPackagesData,
        refund: value
      }
    }
  },
  computed: {
    scrollbarTag () { return this.$store.getters.scrollbarTag }
  },
  methods: {
    openNotificationFailureData (order) {
      common.openNotificationFailureData(this, order)
    },
    changePageNumber (val) {
      if (this.$route.params.warehouseID) {
        this.loadWarehouseShipments(this.paginationObj[val])
      } else {
        this.loadShipments(this.paginationObj[val])
      }
      this.paginationObj.currentPage = val === 'next' ? this.paginationObj.currentPage + 1 : this.paginationObj.currentPage - 1
    },
    changePageSize (val) {
      localStorage.setItem('maxItems', val)
      this.maximumItems = val
      if (this.$route.params.warehouseID) {
        this.loadWarehouseShipments()
      } else {
        this.loadShipments()
      }
    },
    restartOrders (data) {
      const orders = []
      orders.push(data.id)
      sendRequest(false, false, this, 'api/v1/orders/restart-cancelled/', 'post', {orders_id: orders}, true,
        () => {
          this.loadShipments()
        }
      )
    },
    removeAttribute (event) {
      event.preventDefault()
    },
    getStyle (data) {
      return common.getOrderStatusColor(data.status)
    },
    getOrderStatusColor (order) {
      return common.getOrderStatusColor(this.getStatusLabel(order))
    },
    getStatusLabel (order) {
      return common.getStatusLabel(order, this.$store.state.AppActiveUser.userRole)
    },
    onTourComplete () {
      this.$cookies.set('tourMerchantOrders', true, '100y')
      this.$cookies.set('reloaded', true, '100y')
    },
    loadWarehouseShipments (cursorVal) {
      if (!cursorVal) this.paginationObj.currentPage = 1
      this.searchInProgress = true
      const query = `?current_warehouse=${this.$route.params.warehouseID}&cursor=${cursorVal ? cursorVal : ''}&limit=${this.maximumItems}&search=${this.searchVal}&${this.flagFilters.includes('rto_requested') ? 'rto_requested=true' : `status=${this.statuses}`}&${this.criteria}_after=${this.fromDate ? this.fromDate : ''}&${this.criteria}_before=${this.toDate ? this.toDate : ''}`
      sendRequest(false, false, this, `${this.shipmentURL}${query}`, 'get', null, true,
        (response) => {
          this.shipments = response.data.results
          this.paginationObj.currentCursor = cursorVal ? cursorVal : ''
          this.paginationObj.next = response.data.next && new URL(response.data.next).searchParams.get('cursor')
          this.paginationObj.previous = response.data.previous && new URL(response.data.previous).searchParams.get('cursor')
          this.searchedValue = this.searchVal
        }
      )
      this.searchInProgress = false
    },
    handleSearchDate (start, end, criteria) {
      this.flagFilters = []
      this.statuses = []
      this.fromDate = start ? new Date(start).toISOString().split('T')[0] : ''
      this.toDate = end ? new Date(end).toISOString().split('T')[0] : ''
      this.criteria = criteria
      this.$route.params.warehouseID ? this.loadWarehouseShipments() : this.loadShipments()
      this.$router
        .push({
          name: 'merchant-shipments'
        })
        .catch(() => {})
    },
    handleSearch (search) {
      this.searchVal = search.target ? search.target.value : search
      return common.manageSearch(search, this.loadOrdersSearch)
    },
    loadOrdersSearch () {
      if (this.searchInProgress || this.searchedValue === this.searchVal) {
        return
      }
      this.$route.params.warehouseID ? this.loadWarehouseShipments() : this.loadShipments()
    },
    loadShipments (cursorVal) {
      if (!cursorVal) this.paginationObj.currentPage = 1
      this.tableLoader = true
      this.searchInProgress = true
      this.searchVal = typeof this.searchVal === 'string' ? this.searchVal.replaceAll('#', '%23') : this.searchVal
      const query = `?cursor=${cursorVal ? cursorVal : ''}&limit=${this.maximumItems}&${Array.isArray(this.flagFilters) && this.flagFilters.includes('on_hold') ? 'on_hold=true' : this.flagFilters.includes('rto_requested') ? 'rto_requested=true' : `status=${Array.isArray(this.statuses) ? this.statuses.filter(item => item !== 'fulfillment') : ''}`}&${this.criteria}_after=${this.fromDate ? this.fromDate : ''}&${this.criteria}_after=${this.fromDate ? this.fromDate : ''}&${this.criteria}_before=${this.toDate ? this.toDate : ''}&${this.selectedCriteria.value ? this.selectedCriteria.value : this.searchCriteria[0].value}=${this.searchVal}`
      sendRequest(true, false, this, `api/v1/delivery-orders/${query}`, 'get', null, true,
        (response) => {
          this.shipments = response.data.results
          this.paginationObj.currentCursor = cursorVal ? cursorVal : ''
          this.paginationObj.next = response.data.next && new URL(response.data.next).searchParams.get('cursor')
          this.paginationObj.previous = response.data.previous && new URL(response.data.previous).searchParams.get('cursor')
          this.searchedValue = this.searchVal
          this.tableLoader = false
        }
      )
      this.searchInProgress = false 
    },
    returnOrderPackages (data) {
      const returnPackages = {
        data: data.data ? data.data : data,
        title: data.title ? data.title : data.title
      }
      this.governorateId = returnPackages.data ? returnPackages.data.customer.address.zone.city.governorate.id : data.customer.address.zone.city.governorate.id
      this.title = data.title
      this.returnPackagesFlag = true
      this.returnPackagesData = {
        ...returnPackages.data,
        declared_value: 0
      }
    },
    changeStatus () {
      this.flagFilters = []
      this.selected = []
      this.statuses = this.statuses.includes(this.statusValue) ? this.statuses.filter(item => item !== this.statusValue) : [...this.statuses, this.statusValue]
      if (this.statusValue === 'all') this.statuses = []
      if (!this.$route.params.merchantID && !this.$route.params.warehouseID) {
        this.$router
          .push({
            name: 'merchant-shipments',
            query: {
              status: this.statuses
            }
          }).then(() => {
            if (this.fromDate || this.toDate) {
              this.fromDate = ''
              this.toDate = ''
              this.$refs.filterDateModal.resetDate()
            }
            this.loadShipments()
          })
          .catch(() => {})
        
      }
    },
    changeFlag () {
      this.statuses = []
      this.selected = []
      this.flagFilters = this.flagFilters.includes(this.flagValue) ? [] : [this.flagValue]
      if (!this.$route.params.merchantID && !this.$route.params.warehouseID) {
        this.$router
          .push({
            name: 'merchant-shipments',
            query: {
              flag: this.flagFilters
            }
          }).then(() => {
            if (this.fromDate || this.toDate) {
              this.fromDate = ''
              this.toDate = ''
              this.$refs.filterDateModal.resetDate()
            }
            this.loadShipments()
          })
          .catch(() => {})
      }
    },
    viewShipment (data) {
      this.$router.push({
        query: {
          ...this.$route.query,
          order: data.id
        }
      }).catch(() => {})
    },
    updateShipmentStatus (data, sentence, status) {
      this.trackingData = {
        data: data.data ? data.data : data,
        status: status ? status : data.status,
        sentence: sentence ? sentence : data.sentence
      }
      this.$vs.dialog({
        color: 'danger',
        title: i18nData[this.$i18n.locale]['Confirm'],
        text: i18nData[this.$i18n.locale]['Are you sure you want to '] + i18nData[this.$i18n.locale][this.trackingData.sentence],
        acceptText: i18nData[this.$i18n.locale]['Yes'],
        accept: this.confirmUpdateShipmentStatus
      })
    },
    confirmUpdateShipmentStatus (trackingData) {
      this.trackingData = trackingData ? trackingData : this.trackingData
      let trackings = []
      if (this.trackingData.data) {
        trackings = [
          {
            order: this.trackingData.data.id,
            packages: this.trackingData.data.packages.map(item => item.package.id),
            status: this.trackingData.status
          }
        ]
      } else {
        trackings = [
          {
            order: this.trackingData.data.id,
            packages: this.trackingData.data.packages.map(item => item.package.id),
            status: this.trackingData.status
          }
        ]
      }
      sendRequest(false, false, this, 'api/v1/tracking/', 'post', trackings, true,
        () => {
          this.$vs.notify({
            color:'success',
            title:i18nData[this.$i18n.locale]['Success'],
            text:i18nData[this.$i18n.locale]['Delivery Order'] + i18nData[this.$i18n.locale][' has been updated successfully!'],
            position: 'top-center'
          })
          this.loadShipments()
        }
      )
    },
    closeModal () {
      this.returnPackagesFlag = false
      this.loadShipments()
    }
  },
  components: {
    Shipment,
    ReturnAndExchangePackage,
    NewShipBluTable,
    ShipbluCursorPagination,
    ShipbluTr,
    ShipbluTh,
    ShipbluTd,
    ExportButton,
    PrintLabelButton,
    CancelShipmentsButton,
    RequestButton,
    ShipbluPrompt,
    RestartOrders,
    Shepherd,
    TableFilter,
    ResponsiveCheckBox,
    DateFilter,
    DownloadButton,
    OrderViewPanel,
    DownloadLabelsButton,
    NoPermission
  },
  mounted () {
    if (this.$cookies.get('tourMerchantOrders') === 'false') {
      this.$cookies.set('tourMerchantOrders', true, '100y')
    }
    this.$emit('setAppClasses', 'shipment-page')
    if (this.$store.state.AppActiveUser.userRole === 'merchant') {
      this.tourMerchantOrders = (this.$cookies.get('tourMerchantOrders') === 'true')
    }
  },
  created () {
    const vueJs = this
    if (this.$cookies.get('tourMerchantOrders') === 'false') {
      this.tourForDelevryOrders = new Shepherd.Tour({
        useModalOverlay: true,
        defaultStepOptions: {
          cancelIcon: {
            enabled: true
          },
          classes: 'shadow-md bg-purple-dark',
          scrollTo: {behavior: 'smooth', block: 'center'}
        }
      })
      const setpsArray = [
        {
          text: this.$t('Here you can view all your delivery orders.'),
          element:'.step-1',
          button1: this.$t('Cancel'),
          button2: this.$t('Next'),
          next: false
        },
        {
          text: this.$t('You can also filter by their status'),
          element: '.step-2',
          button1: this.$t('Back'),
          button2: this.$t('Next'),
          next: true
        },
        {
          text: this.$t('Now, print your shipping label'),
          element: '.step-3',
          button1: this.$t('Back'),
          button2: this.$t('Next'),
          next: true
        },
        {
          text: this.$t('If your profile is reviewed and verified, you can request pickup here.'),
          element: '.step-4',
          button1: this.$t('Back'),
          button2: this.$t('Next'),
          next: true,
          router: 'merchant-supplies-shop'
        },
        {
          text: this.$t('You may request flyers or buy shipping boxes and supplies from here.'),
          element: '.Supplies',
          button1: this.$t('Back'),
          button2: this.$t('Next'),
          next: true,
          router: 'merchant-account'

        },
        {
          text: this.$t('Click here to add a payout method, know when you will get your COD and explore your pricing plan.'),
          element: '.Financials',
          button1: this.$t('Back'),
          button2: this.$t('Next'),
          next: true
        },
        {
          text: this.$t('If you have larger monthly volume of shipping, contact our sales team to receive discounts. Thank you for choosing ShipBlu. We look forward to serve you!'),
          element: '.step-7',
          button1: this.$t('Back'),
          button2: this.$t('Done!'),
          next: true
        }
      ]
      setpsArray.forEach((element) => { 
        this.tourForDelevryOrders.addStep({
          text: element.text,
          attachTo: {
            element: element.element,
            on: 'bottom'
          },
          buttons: [
            {
              action () {
                return element.next ? this.back() : this.cancel()
              },
              classes: 'shepherd-button-secondary',
              text: element.button1
            },
            {
              action () {
                if (element.router) {
                  vueJs.$router.push({
                    name: element.router
                  }).catch(() => {})
                }
              },
              text: element.button2
            }
          ]
        })
      })
    }
    const merchantData = common.checkMerchantInLocalStorage(this)
    let user = {}
    merchantData.then(results => {
      user = results.merchantData
      this.merchantID = user.id
      this.merchantStatus = user.status
      this.merchant = user
    })
    this.$router.push({
      query: {...this.$route.query}
    }).catch(() => {})
    if (this.$cookies.get('reloaded') === 'false') {
      this.$cookies.set('reloaded', true)
    }
    this.tourMerchantOrders = (this.$cookies.get('tourMerchantOrders') === 'true')
    if (this.$route.params.warehouseID) {
      this.loadWarehouseShipments()
    } else if (this.$route.query.flag) {
      this.flagFilters = typeof (this.$route.query.flag) === 'string' ? Array(this.$route.query.flag) : this.$route.query.flag
      this.loadShipments()
    } else if (this.$route.query.status) {
      this.statuses = typeof (this.$route.query.status) === 'string' ? Array(this.$route.query.status) : this.$route.query.status
      this.loadShipments()
    } else {
      this.flagFilters = []
      this.statuses = []
      this.loadShipments()
    }

    if (this.$route.query.order) {
      this.showPanel = true
    } else {
      this.showPanel = false
    }
  }
}
</script>

<style scoped>
.vs-dialog {
  max-width: 530px;
}
.bg-orange{
  background: #F58231 !important;
}

</style>
<style lang="scss">
@import "@/assets/scss/vuexy/apps/email.scss";
#order-label-print{
  display: none;
  visibility: hidden;
}
.con-pagination-table{
  display: none;
}
.exchange-modal {
  width: 500px;
  position: fixed;
  top: calc(50% - 58.605px);
  right: calc(50% - 250px);
  z-index: 999999;
  background: #FFFFFF;
  box-shadow: 0px 2px 16px rgba(157, 173, 194, 0.38);
  transition: all 1s ease-in-out;
  animation: hideAnimation 0s ease-in-out 4s;
  animation-fill-mode: forwards;
  p {
    color: #6C84A3;
  }
}
.show-exchange-modal {
  visibility: visible;
  opacity: 1 !important;
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: rgba(10, 50, 102, 0.24);
  backdrop-filter: blur(2px);
  opacity: 0;
  animation: hideAnimation 0s ease-in-out 4s;
  animation-fill-mode: forwards;
}
@keyframes hideAnimation {
  to {
    visibility: hidden;
    width: 0;
    height: 0;
    transition: all 1s ease-in-out;
  }
}
@media (max-width: 767px) {
  .exchange-modal {
    width: unset;
    max-width: 300px;
    right: calc(50% - 150px);
  }
}
.title {
  background: rgba(220, 238, 255, 0.5);
  border-radius: 6px 0px 0px 6px;
  color: '#1C5BFE'
}
.shepherd-enabled.shepherd-element {
    z-index: 51900;
}
.shepherd-modal-is-visible{
    z-index: 51900;
}
.shepherd-text {
    padding: 0 2em;
}
.shepherd-header {
    padding: 0 0.75rem;
    height: 35px;
}
.shepherd-footer{
  margin-top: 16px;
}
.shepherd-button {
  background: #1C5BFE;
}
.filter-z-index {
  z-index: 9999;
  user-select: none;
}
</style>